/**
 * ISO 3166-1 alpha-2 → a flag glyph plus an accessible country name.
 *
 * `jobs.country` became a descriptive field on 2026-09-01 (it was previously a
 * partner-isolation marker — see `src/lib/db/queries/partner-isolation.ts`) and
 * is now populated on ~2,900 active jobs, derived from `company_location` by
 * `src/lib/jobs/company-location-country.ts`. This renders it.
 *
 * ## Why a computed glyph and not an icon set or a lookup table of images
 *
 * A regional-indicator pair is derived arithmetically from the two letters, so
 * every valid code works with no asset, no dependency, and no bundle cost — it
 * matters on a page with a 10 MiB worker budget and ~20 rows per render.
 *
 * ## The degradation, stated because it is not a bug
 *
 * Windows has no flag glyphs in its default emoji font, so `🇺🇸` renders there
 * as the letters "US". That is a reasonable fallback — the reader still gets
 * the country — and it is why the NAME is carried separately as the accessible
 * label rather than relying on the glyph to convey meaning. A screen reader
 * announces the name, never the raw indicator pair.
 */

/**
 * Display names for the codes the corpus actually produces. Measured on prod
 * 2026-09-02 after the backfill: US 1,506, SG 221, MY 141, ID 131, CA 112,
 * GB 106, IE 97, FR 71, DE 46, NL 34, then a thin tail.
 *
 * A code with no entry still renders its flag, labelled with the bare code —
 * degraded but never wrong, which is the right trade for a long tail nobody
 * has seen yet.
 */
const COUNTRY_NAMES: Record<string, string> = {
	US: "United States",
	SG: "Singapore",
	MY: "Malaysia",
	ID: "Indonesia",
	CA: "Canada",
	GB: "United Kingdom",
	IE: "Ireland",
	FR: "France",
	DE: "Germany",
	NL: "Netherlands",
	DK: "Denmark",
	SE: "Sweden",
	TH: "Thailand",
	AU: "Australia",
	BR: "Brazil",
	CH: "Switzerland",
	VG: "British Virgin Islands",
	AR: "Argentina",
	GR: "Greece",
	JP: "Japan",
	UA: "Ukraine",
	ES: "Spain",
	SC: "Seychelles",
	CZ: "Czech Republic",
	IL: "Israel",
	BE: "Belgium",
	AE: "United Arab Emirates",
	TR: "Turkey",
	AM: "Armenia",
	UG: "Uganda",
	PL: "Poland",
	PH: "Philippines",
	LU: "Luxembourg",
	LB: "Lebanon",
	KE: "Kenya",
	IN: "India",
	FI: "Finland",
	EG: "Egypt",
	CY: "Cyprus",
};

/** Distance from ASCII 'A' to the first regional indicator code point. */
const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 0x41;

export type CountryFlag = {
	/** The regional-indicator pair. Decorative — always paired with `label`. */
	glyph: string;
	/** Human-readable country name, or the bare code when unmapped. */
	label: string;
};

/**
 * Returns `null` for anything that is not exactly two ASCII letters, so a
 * malformed or absent value renders nothing rather than a broken glyph. The
 * caller decides what "nothing" looks like.
 */
export function countryFlag(
	code: string | null | undefined,
): CountryFlag | null {
	if (!code) return null;
	const upper = code.trim().toUpperCase();
	// Exactly two A-Z. A three-letter code or a name would otherwise produce
	// mojibake from the arithmetic below.
	if (!/^[A-Z]{2}$/.test(upper)) return null;

	const glyph = String.fromCodePoint(
		upper.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET,
		upper.charCodeAt(1) + REGIONAL_INDICATOR_OFFSET,
	);
	return { glyph, label: COUNTRY_NAMES[upper] ?? upper };
}
